import { Menu, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import { Fragment, FunctionComponent, PropsWithChildren, useMemo } from 'react';
import { UploadStatus } from '../../services/upload/utils';
import { FiCheck, FiLoader, FiUpload, FiX } from 'react-icons/fi';
import { useUploadSessions } from '../../services/upload/UploadProvider';
import { OneDriveUploadSession } from '../../services/upload/OneDriveUploadProvider';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { StatusBar } from './StatusBar';
import { splitName } from '../../utils/text';

interface StatusMenuProps {}

const MenuItemContainer: FunctionComponent<PropsWithChildren> = ({ children }) => {
  return (
    <div
      className="py-2 px-3 text-primary-800 font-semibold 
    bg-primary-std border-b flex items-center space-x-2"
    >
      {children}
    </div>
  );
};

const StatusMenu: FunctionComponent<StatusMenuProps> = () => {
  const sessions = useUploadSessions<OneDriveUploadSession>();

  const sessionsToShow = useMemo(() => {
    const filtered = sessions.filter(s => s.status !== UploadStatus.Cancelled);
    const sorted = filtered.sort((a, b) => {
      if (a.status === UploadStatus.Complete) {
        return 1;
      } else if (b.status === UploadStatus.Complete) {
        return -1;
      }

      return 0;
    });

    return sorted;
  }, [sessions]);

  const menuIconContainerClass = 'absolute bottom-0 right-0 h-5 w-5 z-20 rounded-full text-white p-1';
  const menuIconClass = 'h-full w-full';
  let menuIcon: JSX.Element = <></>;
  if (sessions.find(s => s.status === UploadStatus.Failed)) {
    menuIcon = (
      <div className={clsx(menuIconContainerClass, 'bg-secondary-800')}>
        <FiX className={clsx(menuIconClass)} />
      </div>
    );
  } else if (sessions.find(s => s.status === UploadStatus.Interrupted)) {
    menuIcon = (
      <div className={clsx(menuIconContainerClass, 'bg-primary-800')}>
        <FiX className={clsx(menuIconClass)} />
      </div>
    );
  } else if (sessions.find(s => s.status === UploadStatus.Waiting || s.status === UploadStatus.Uploading)) {
    menuIcon = (
      <div className={clsx(menuIconContainerClass, 'bg-primary-800 p-1')}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{
            repeat: Infinity,
            ease: 'linear',
            duration: 2
          }}
        >
          <FiLoader className={clsx(menuIconClass)} />
        </motion.div>
      </div>
    );
  } else if (sessions.find(s => s.status === UploadStatus.Complete)) {
    menuIcon = (
      <div className={clsx(menuIconContainerClass, 'bg-primary-800')}>
        <FiCheck className={clsx(menuIconClass)} />
      </div>
    );
  }

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="h-full flex items-center gap-2 text-right text-primary-800 font-bold">
        <div className="w-10 h-10 p-1 relative">
          <FiUpload className="w-full h-full" />
          {menuIcon}
        </div>
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 rounded-md overflow-hidden shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 flex flex-col w-64">
          {sessionsToShow.find(s => s.status === UploadStatus.Waiting || s.status === UploadStatus.Uploading) && (
            <MenuItemContainer>
              <StatusBar />
            </MenuItemContainer>
          )}
          {sessionsToShow.map(session => (
            <MenuItemContainer key={session.id}>
              <p className="flex-1">{splitName(session.name)}</p>
              {session.status === UploadStatus.Complete && <FiCheck className="w-4 h-4" />}
              {(session.status === UploadStatus.Failed ||
                session.status === UploadStatus.Interrupted ||
                session.status === UploadStatus.Cancelled) && <FiX className="w-4 h-4" />}
              {session.status === UploadStatus.Uploading && (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    repeat: Infinity,
                    ease: 'linear',
                    duration: 2
                  }}
                >
                  <FiLoader className="w-6 h-6" />
                </motion.div>
              )}
              {session.status === UploadStatus.Waiting && <FiLoader className="w-6 h-6" />}
            </MenuItemContainer>
          ))}
          <MenuItemContainer>
            <Link to={'/documents/upload'} className="flex items-center space-x-2 ml-auto mr-auto">
              <p>Go to uploads</p>
              <FiUpload />
            </Link>
          </MenuItemContainer>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default StatusMenu;
